/**
 * Packing a folder of numbered frames into one sheet (spec 034): a square-ish
 * grid of cells the size of the largest frame, each frame centred in its
 * cell, left to right then top to bottom. The packed sheet is cut by grid.
 */

import type { SheetCut, SpriteSize } from '../../../Shared/src/@types';
import { IMPORT_DEFAULT_FPS } from '../../../Shared/src/sheets/sheetImport';

export interface PackLayout {
  columns: number;
  rows: number;
  cellWidth: number;
  cellHeight: number;
  /** The packed sheet's size: columns × cell, rows × cell. */
  width: number;
  height: number;
  /** Each frame's top-left corner in the sheet, in frame order. */
  positions: { x: number; y: number }[];
}

/** `columns = ceil(√n)`, `rows = ceil(n ÷ columns)`, a cell of the largest width × height. */
export function packLayout(sizes: readonly SpriteSize[]): PackLayout {
  const n = sizes.length;
  const columns = Math.max(1, Math.ceil(Math.sqrt(n)));
  const rows = Math.max(1, Math.ceil(n / columns));
  const cellWidth = Math.max(1, ...sizes.map((s) => s.width));
  const cellHeight = Math.max(1, ...sizes.map((s) => s.height));
  const positions = sizes.map((s, i) => ({
    x: (i % columns) * cellWidth + Math.floor((cellWidth - s.width) / 2),
    y: Math.floor(i / columns) * cellHeight + Math.floor((cellHeight - s.height) / 2),
  }));
  return { columns, rows, cellWidth, cellHeight, width: columns * cellWidth, height: rows * cellHeight, positions };
}

/** The grid cut a packed sheet of `n` frames gets (D-3: 12 fps). */
export function packedCut(layout: Pick<PackLayout, 'columns' | 'rows'>, n: number): SheetCut {
  return { cutBy: 'grid', columns: layout.columns, rows: layout.rows, frameCount: n, fps: IMPORT_DEFAULT_FPS, source: 'frames' };
}

/** Draw the frames on a canvas by `packLayout` and return the PNG with its grid cut. */
export async function packFrames(blobs: readonly Blob[]): Promise<{ blob: Blob; cut: SheetCut; size: SpriteSize }> {
  const images = await Promise.all(blobs.map((b) => createImageBitmap(b)));
  try {
    const layout = packLayout(images.map((img) => ({ width: img.width, height: img.height })));
    const canvas = document.createElement('canvas');
    canvas.width = layout.width;
    canvas.height = layout.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser cannot pack frames (no 2D canvas)');
    images.forEach((img, i) => ctx.drawImage(img, layout.positions[i].x, layout.positions[i].y));
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('The packed sheet could not be encoded'))), 'image/png'),
    );
    return { blob, cut: packedCut(layout, images.length), size: { width: layout.width, height: layout.height } };
  } finally {
    images.forEach((img) => img.close?.());
  }
}
